/**
 * Markdown renderer tuned for in-app documentation.
 *
 * Key behaviours:
 *  - `<a href="/...">` is rewritten to a React Router <Link> so
 *    deep-links inside our docs (e.g. "→ Apri Esecuzioni filtrate")
 *    navigate without a full reload.
 *  - External links open in a new tab with `rel="noreferrer"`.
 *  - GitHub-Flavoured Markdown is enabled (tables, checklists,
 *    autolinked URLs).
 *  - Code blocks (fenced) and inline `code` get our muted-pill
 *    treatment that already lives in globals.css for inline `<code>`.
 *
 * The wrapping div carries `prose-*` style overrides via Tailwind
 * arbitrary selectors so we don't need the @tailwindcss/typography
 * plugin for the half-dozen tweaks we actually need.
 */
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/cn";

interface MarkdownRendererProps {
  source: string;
  className?: string;
}

export function MarkdownRenderer({ source, className }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        // Reset typography — we control everything explicitly.
        "max-w-3xl text-sm leading-relaxed text-foreground",
        // Headings
        "[&_h1]:mt-0 [&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:tracking-tight",
        "[&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-tight",
        "[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold",
        "[&_h4]:mt-4 [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold",
        // Paragraphs + spacing
        "[&_p]:my-3",
        "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5",
        "[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5",
        "[&_li]:my-1",
        // Tables
        "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
        "[&_th]:border-b [&_th]:border-border [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-medium [&_th]:text-muted-foreground",
        "[&_td]:border-b [&_td]:border-border/60 [&_td]:px-2 [&_td]:py-1.5 [&_td]:align-top",
        // Blockquotes (used for tips / warnings via the `> 💡` /
        // `> ⚠️` convention in the source)
        "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:bg-muted/30 [&_blockquote]:px-3 [&_blockquote]:py-2 [&_blockquote]:text-muted-foreground",
        // Code blocks (fenced)
        "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-border/60 [&_pre]:bg-muted/40 [&_pre]:p-3 [&_pre]:text-xs [&_pre]:leading-relaxed",
        "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:font-mono",
        // Horizontal rule
        "[&_hr]:my-6 [&_hr]:border-t [&_hr]:border-border",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children, ...props }) {
            // Internal admin link → React Router (no full reload).
            if (href && href.startsWith("/")) {
              return (
                <Link
                  to={href}
                  className="text-accent underline underline-offset-2 hover:no-underline"
                  {...(props as object)}
                >
                  {children}
                </Link>
              );
            }
            // External / anchor link.
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline underline-offset-2 hover:no-underline"
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
